import { useState } from "react";
import { intl } from "LocaleWrapper";
import { Alert, alertClasses, Box, Collapse, IconButton, Typography } from "@mui/material";
import {
	TimelineItem,
	TimelineSeparator,
	TimelineConnector,
	TimelineContent,
	TimelineOppositeContent,
	TimelineDot,
} from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DataFormat } from "imports/Utils";
import { FormattedCalendarDate, FormattedDate } from "utils/DateUtils";
import { AnglerEvents, getInitialTagSettings, VoyagePlanMeasuringPointEvent } from "../../AnglerUtils";

const AnglerTimelineMeasuringPointEvent = ({ event }: { event: VoyagePlanMeasuringPointEvent }) => {
	const [ expanded, setExpanded ] = useState(false);

	const hasMeasurementData = !!event.measurement?.waterLevel;

	return (
		<TimelineItem>
			<TimelineOppositeContent sx={{ m: "auto 0" }}>
				<Typography variant="h6" color="textPrimary" sx={{ fontSize: 12 }}>
					<FormattedCalendarDate date={event.startTime} />
				</Typography>
			</TimelineOppositeContent>

			<TimelineSeparator>
				<TimelineConnector />
				<TimelineDot sx={{ backgroundColor: getInitialTagSettings()[AnglerEvents.MeasuringPoint].color }}>
					{getInitialTagSettings()[AnglerEvents.MeasuringPoint].icon}
				</TimelineDot>
				<TimelineConnector />
			</TimelineSeparator>

			<TimelineContent sx={{ m: "auto 0" }}>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Typography variant="h6" color="textPrimary" sx={{ fontSize: 12 }}>
						{event.name}
					</Typography>

					{hasMeasurementData && (
						<IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ ml: 0.5, p: 0.2 }}>
							{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
						</IconButton>
					)}
				</Box>

				{hasMeasurementData && (
					<>
						<Typography variant="h6" color="textSecondary" sx={{ fontSize: 11 }}>
							{DataFormat.simpleUnit(event.measurement?.waterLevel, DataFormat.UNITS.CENTIMETER)}
						</Typography>

						<Collapse in={expanded} timeout="auto" unmountOnExit>
							<AnglerTimelineMeasuringPointEventInformation event={event} />
						</Collapse>
					</>
				)}

				{event.warning?.message && (
					<Alert
						severity="warning"
						variant="standard"
						sx={{
							mt: 1,
							mb: 1,
							py: 0,
							px: 1,
							fontSize: 10,
							[`& .${alertClasses.icon}`]: { fontSize: 16, mr: 0.5, display: "flex", alignItems: "center" },
						}}>
						{event.warning.message}
					</Alert>
				)}
			</TimelineContent>
		</TimelineItem>
	);
};

export const AnglerTimelineMeasuringPointEventInformation = ({ event }: { event: VoyagePlanMeasuringPointEvent }) => (
	<>
		{event.measurement?.waterwayDepthCm && (
			<Typography variant="h6" color="textSecondary" sx={{ fontSize: 11 }}>
				{`${intl.formatMessage({ id: "ANGLER_WATERWAY_DEPTH_IS" })} ${DataFormat.simpleUnit(event.measurement.waterwayDepthCm, DataFormat.UNITS.CENTIMETER)}`}
			</Typography>
		)}

		{event.keelClearanceCm && (
			<Typography variant="h6" color="textSecondary" sx={{ fontSize: 11 }}>
				{`${intl.formatMessage({ id: "ANGLER_KEEL_CLEARANCE" })} ${DataFormat.simpleUnit(event.keelClearanceCm, DataFormat.UNITS.CENTIMETER)}`}
			</Typography>
		)}

		{event.measurement?.timestamp && (
			<Typography variant="h6" color="textSecondary" sx={{ fontSize: 11 }}>
				<FormattedDate date={event.measurement.timestamp} />
			</Typography>
		)}
	</>
);

export default AnglerTimelineMeasuringPointEvent;
